import type { CartItem } from '@features/cart/types';
import { resolveLineShopId, resolveLocalProductId } from '@core/utils/cartLineUtils';

function normalizePart(value: string | number | null | undefined): string {
  return String(value ?? '').trim().toLowerCase();
}

export function createCartLineKey(item: Omit<CartItem, 'id'> | CartItem): string {
  const shopId = resolveLineShopId(item as CartItem);
  const localProductId = resolveLocalProductId(item.productId);
  const color = normalizePart(item.color);
  const size = normalizePart(item.size);
  return [shopId, localProductId, `color:${color}`, `size:${size}`].join('|');
}

export function normalizeCartItem(item: CartItem | Omit<CartItem, 'id'>): CartItem {
  const withShop = {
    ...item,
    shopId: resolveLineShopId(item as CartItem),
  };
  return {
    ...withShop,
    id: createCartLineKey(withShop),
    quantity: Math.max(1, Number(withShop.quantity) || 1),
    price: Number(withShop.price) || 0,
  };
}

export function normalizeCartItems(items: readonly CartItem[]): CartItem[] {
  return mergeCartItems([], items);
}

export function mergeCartItems(
  baseItems: readonly CartItem[],
  incomingItems: readonly CartItem[]
): CartItem[] {
  const byKey = new Map<string, CartItem>();

  for (const raw of [...baseItems, ...incomingItems]) {
    const item = normalizeCartItem(raw);
    const existing = byKey.get(item.id);
    if (!existing) {
      byKey.set(item.id, item);
      continue;
    }
    byKey.set(item.id, {
      ...existing,
      ...item,
      quantity: existing.quantity + item.quantity,
    });
  }

  return [...byKey.values()];
}

export function replaceOrAppendCartItem(
  items: readonly CartItem[],
  item: Omit<CartItem, 'id'> | CartItem
): CartItem[] {
  const normalized = normalizeCartItem(item);
  const next = [...items];
  const index = next.findIndex(line => line.id === normalized.id);
  if (index === -1) return [...next, normalized];

  next[index] = {
    ...next[index],
    ...normalized,
    quantity: next[index].quantity + normalized.quantity,
  };
  return next;
}

export function updateCartLineQuantity(
  items: readonly CartItem[],
  id: string,
  quantity: number
): CartItem[] {
  if (quantity <= 0) return items.filter(item => item.id !== id);
  return items.map(item =>
    item.id === id ? { ...item, quantity: Math.max(1, quantity) } : item
  );
}

export function getCartItemCount(items: readonly CartItem[]): number {
  return items.reduce((total, item) => total + item.quantity, 0);
}

export function getCartTotal(items: readonly CartItem[]): number {
  return items.reduce((total, item) => total + item.price * item.quantity, 0);
}
